// Unpublished Work © 2023-2024 Deere & Company.
import { Alert } from "@deere/fuel-react";
import { Box, Breadcrumbs, Button, Grid, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useOktaAuth } from "@okta/okta-react";
import { AxiosResponse } from "axios";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import buildInvoiceLineItemData from "./components/buildInvoiceLineItemData";
import InvoiceLineItems from "./components/InvoiceLineItems";
import {
  InvoiceHeaderDetailsV2,
  InvoiceLineItem,
} from "../../../api/invoices/interfaces";
import invoiceApi from "../../../api/invoices/invoiceApi";
import { BusinessAction, RBACRole } from "../../../api/rbac/types";
import Address from "../../../components/address/Address";
import DetailedInvoiceSummary from "../../../components/DetailedInvoiceSummary/DetailedInvoiceSummary";
import ShippingInformation from "../../../components/ShippingInformation/ShippingInformation";
import SummarySection, {
  SummaryItem,
} from "../../../components/SummarySection/SummarySection";
import DownloadButton from "../../../components/Table/components/DownloadButton/DownloadButton";
import { NoAccessError } from "../../../context/AccessContext";
import { useErrorStatus } from "../../../context/AuthorizationContext";
import { ConfigContext } from "../../../context/ConfigContext";
import { DealerAccountContext } from "../../../context/DealerAccountContext";
import { FeatureToggleContextV2 } from "../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { RBACAccessContext } from "../../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../../context/UserContext/UserContext";
import useQuery from "../../../hooks/useQuery/useQuery";
import {
  BRAZIL_DEALER_LOCATION,
  BRAZIL_EXPORT_MRUS,
  BRAZIL_NFE_ATTACHMENT,
  CANADA_DEALER_LOCATION,
  CHINA_DEALER_LOCATION,
  INDIA_DEALER_LOCATION,
  INDIA_GST_ANNEXURE_INVOICE,
  INDIA_GST_TRADE_INVOICE,
  US_DEALER_LOCATION,
} from "../../../utils/constants";
import { BlobResponse } from "../../../utils/downloadFile";
import { formatDate } from "../../../utils/formatters";
import { invoiceTypeHasNfeAttachment } from "../../../utils/invoices/invoiceUtils";
import { CATEGORIES, TOGGLES } from "../../featureToggles/constants";

function isOrderInvoiceType(invoiceType: string) {
  return (
    invoiceType === "Order Invoice" ||
    invoiceType === "Cancelled - Order Invoice"
  );
}

const InvoiceDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { invoiceNumber } = useParams();
  const { authState } = useOktaAuth();
  const xid = authState?.accessToken?.claims.userID as string;
  const { dealerAccount, dealerLocation, selectedAccounts } =
    useContext(DealerAccountContext);
  const isUSDealer = dealerLocation === US_DEALER_LOCATION;
  const isCanadaDealer = dealerLocation === CANADA_DEALER_LOCATION;
  const isChinaDealer = dealerLocation === CHINA_DEALER_LOCATION;
  const isBrazilDealer = dealerLocation === BRAZIL_DEALER_LOCATION;
  const isIndiaDealer = dealerLocation === INDIA_DEALER_LOCATION;
  const isBrazilExportDealer = BRAZIL_EXPORT_MRUS.includes(
    dealerAccount?.mruSalesOrg || ""
  );
  const accessToken = authState?.accessToken?.accessToken;
  const { setErrorStatusCode } = useErrorStatus();
  const {
    INVOICE_SERVICE_URL,
    PSTS_URL,
    ENABLE_CLAIMS_AUTHORIZATION,
    ENABLE_INVOICES_VIEWED,
    ENABLE_INVOICE_LEADING_ZEROS,
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_CLAIMS_CHINA,
    ENABLE_CLAIMS_INDIA,
    ENABLE_INVOICE_DETAILS_PORTLAND_SURCHARGE,
    ENABLE_INVOICES_LINE_ITEM_PART_DESCRIPTION_TRANSLATION,
  } = useContext(ConfigContext);
  const { t, i18n } = useTranslation(["invoices", "common"]);
  const componentRef = useRef<null | HTMLDivElement>(null);
  const { isToggleEnabled } = useContext(FeatureToggleContextV2);
  const user = useContext(UserContext);
  const [disableActions, setDisableActions] = useState<boolean>(true);
  const { isRBACEnabled } = useContext(RBACAccessContext);

  const isCorporateManager =
    isRBACEnabled && !!user?.is(RBACRole.CORPORATE_PARTS_MANAGER);
  const isMultipleAccountsSelected =
    isCorporateManager && selectedAccounts.length > 1;
  const dealerAccountId = isMultipleAccountsSelected
    ? selectedAccounts.map((acc) => acc.id).toString()
    : (dealerAccount?.id as string);

  useEffect(() => {
    if (isChinaDealer) {
      setDisableActions(ENABLE_CLAIMS_CHINA);
    }
    if (isIndiaDealer) {
      setDisableActions(ENABLE_CLAIMS_INDIA);
    }
  }, []);

  const apiFnInvoiceDetailsV2 = (
    token: string,
    lang: string
  ): Promise<InvoiceHeaderDetailsV2> =>
    invoiceApi.getInvoiceDetailsV2(
      INVOICE_SERVICE_URL,
      invoiceNumber || "",
      token,
      dealerAccountId,
      dealerAccount?.mruSalesOrg || "",
      dealerLocation,
      lang,
      ENABLE_INVOICE_LEADING_ZEROS,
      ENABLE_INVOICE_DETAILS_PORTLAND_SURCHARGE
    );

  const {
    data: invoice,
    loading: loadingInvoiceDetails,
    forbidden,
  } = useQuery<InvoiceHeaderDetailsV2>({
    apiFn: apiFnInvoiceDetailsV2,
    authorizationEnabled: ENABLE_CLAIMS_AUTHORIZATION,
    accountId: dealerAccountId,
  });

  const apiFnInvoiceLineItems = (
    token: string,
    lang: string
  ): Promise<InvoiceLineItem[]> =>
    invoiceApi.getInvoiceLineItems(
      INVOICE_SERVICE_URL,
      invoiceNumber || "",
      token,
      dealerLocation,
      lang,
      dealerAccount?.id,
      ENABLE_INVOICES_LINE_ITEM_PART_DESCRIPTION_TRANSLATION,
      ENABLE_INVOICE_DETAILS_PORTLAND_SURCHARGE
    );

  const {
    data: invoiceLineItems,
    error,
    reload,
    loading: loadingInvoiceLineItems,
  } = useQuery<InvoiceLineItem[]>({
    apiFn: apiFnInvoiceLineItems,
    authorizationEnabled: ENABLE_CLAIMS_AUTHORIZATION,
    accountId: dealerAccountId,
  });

  useEffect(() => {
    if (
      !loadingInvoiceDetails &&
      !forbidden &&
      ENABLE_INVOICES_VIEWED &&
      (isUSDealer || isCanadaDealer)
    ) {
      invoiceApi.invoiceViewed(
        INVOICE_SERVICE_URL,
        invoiceNumber,
        dealerAccountId,
        xid,
        dealerLocation,
        accessToken,
        i18n.language
      );
    }
  }, [loadingInvoiceDetails, forbidden]);

  const getDetailsForOrderInvoice = () => {
    const orderTypes = Array.isArray(invoice?.orderType)
      ? invoice.orderType
      : [invoice?.orderType ?? ""];
    const summaryDetails: Array<SummaryItem> = [
      {
        title: t("invoices:invoiceDetails.label.summary.invoiceNumber"),
        value: invoice?.invoiceNumber,
      },
      {
        title: t("invoices:invoiceDetails.label.summary.orderType"),
        value: orderTypes
          .map((orderType) =>
            t(
              `invoices:invoiceDetails.summary.orderType.${orderType.replace(
                / /g,
                ""
              )}`
            )
          )
          .join(", "),
      },
      {
        title: t("invoices:invoiceDetails.label.summary.invoiceType"),
        value: t(
          `invoices:invoiceDetails.summary.invoiceType.${invoice?.invoiceType
            .replace(/ /g, "")
            .replace("-", "")}`
        ),
      },
      {
        title: t("invoices:invoiceDetails.label.summary.invoiceDate"),
        value: formatDate(invoice?.invoiceDate, i18n.language),
      },
    ];

    if (isBrazilExportDealer) {
      summaryDetails.splice(1, 0, {
        title: t(
          "invoices:invoiceDetails.label.summary.officialDocumentNumber"
        ),
        value: invoice?.officialDocumentNumber,
      });
    }

    if (isBrazilDealer) {
      summaryDetails.push({
        title: t("invoices:invoiceDetails.label.summary.nfeNumber"),
        value: invoice?.nfeNumber,
      });
    }

    if (isChinaDealer) {
      summaryDetails.push({
        title: t("invoices:invoiceDetails.label.summary.deliveryNumber"),
        value: [
          ...new Set(
            invoiceLineItems?.map((lineItem) => lineItem.deliveryNumber) ?? ""
          ),
        ].join(", "),
      });

      const trackingUrl = `${PSTS_URL}/jumpShipTrackSearchJsp?invoiceNums=${invoiceNumber}`;
      summaryDetails.push({
        title: t("invoices:invoiceDetails.label.summary.tracking"),
        value: t("invoiceDetails.value.summary.carrierTracking"),
        isLink: true,
        onClick: () => window.open(trackingUrl, "_blank", "noreferrer"),
      });
    }
    return summaryDetails;
  };

  const getDetailsForCreditOrDebitMemo = () => {
    const summaryDetails = [
      {
        title: t("invoices:invoiceDetails.label.summary.invoiceNumber"),
        value: invoice?.originalInvoiceNum,
      },
      {
        title: t("invoices:invoiceDetails.label.summary.originalInvoiceNumber"),
        value: invoice?.invoiceNumber,
      },
      {
        title: t("invoices:invoiceDetails.label.summary.invoiceType"),
        value: t(
          `invoices:invoiceDetails.summary.invoiceType.${invoice?.invoiceType
            .replace(/ /g, "")
            .replace("-", "")}`
        ),
      },
      {
        title: t("invoices:invoiceDetails.label.summary.invoiceDate"),
        value: formatDate(invoice?.invoiceDate, i18n.language),
      },
    ];

    if (isBrazilExportDealer) {
      summaryDetails.splice(1, 0, {
        title: t(
          "invoices:invoiceDetails.label.summary.officialDocumentNumber"
        ),
        value: invoice?.officialDocumentNumber,
      });
    }

    if (isBrazilDealer) {
      summaryDetails.push(
        {
          title: t("invoices:invoiceDetails.label.summary.nfeNumber"),
          value: invoice?.originalNfeNumber,
        },
        {
          title: t("invoices:invoiceDetails.label.summary.newNfeNumber"),
          value: invoice?.nfeNumber,
        }
      );
    }
    return summaryDetails;
  };

  const invoiceSummaryDetails = isOrderInvoiceType(invoice?.invoiceType ?? "")
    ? getDetailsForOrderInvoice()
    : getDetailsForCreditOrDebitMemo();

  if (forbidden) {
    const title = t("common:unAuthorizedErrorPage.title");
    const subtitles = [
      t("common:unAuthorizedErrorPage.invoiceDetails.subTitle"),
    ];
    return (
      <NoAccessError
        title={title}
        subtitles={subtitles}
        showReturnHomeButton={false}
      />
    );
  }

  const downloadFunction = (
    token: string,
    lang?: string,
    objTypeCode?: string,
    fileName?: string
  ): Promise<AxiosResponse<BlobResponse>> =>
    invoiceApi.getInvoicePDF(
      INVOICE_SERVICE_URL,
      invoiceNumber || "",
      dealerAccountId,
      token,
      objTypeCode,
      dealerLocation,
      lang,
      fileName
    );

  const downloadAttachmentFunction = (
    token: string,
    lang?: string,
    _objTypeCode?: string,
    fileName?: string,
    officialDocumentNumber?: string
  ): Promise<AxiosResponse<BlobResponse>> =>
    invoiceApi.getInvoiceAttachment(
      INVOICE_SERVICE_URL,
      invoiceNumber || "",
      token,
      fileName ?? "",
      officialDocumentNumber ?? "",
      dealerAccountId,
      lang
    );

  const setShowUnauthorizedError = (isUnauthorized: boolean) => {
    if (isUnauthorized) setErrorStatusCode(401);
  };

  const isOrderInvoice = (): boolean =>
    invoice?.invoiceType === "Order Invoice";

  const isClaimsByInvoiceFlow =
    (ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
      !isToggleEnabled(
        CATEGORIES.CLAIMS_SERVICE,
        TOGGLES.ENABLE_CLAIMS_BY_HU
      )) ||
    !ENABLE_FEATURE_TOGGLE_CONTEXT_V2;

  const isUserAuthorizedToCreateClaim =
    (isRBACEnabled && user?.isAuthorizedTo(BusinessAction.CREATE_CLAIM)) ||
    !isRBACEnabled;

  const claimsTimeLimitExceeded = invoice?.timeLimit?.claimsExceeded ?? false;

  const shouldShowTimeLimitErrorMessage =
    isClaimsByInvoiceFlow &&
    isOrderInvoice() &&
    isUserAuthorizedToCreateClaim &&
    !invoice?.reversed &&
    disableActions &&
    claimsTimeLimitExceeded;

  return (
    <Grid data-testid="invoice-details-page">
      <Breadcrumbs data-testid="invoice-details-breadcrumb">
        <Button
          onClick={() => navigate(`/invoices`)}
          data-testid="all-invoices-breadcrumb"
          variant="text-inline"
        >
          {t("invoices:breadcrumbs.invoices")}
        </Button>
        <Typography sx={{ color: theme.tokens.FuelColorTextPrimary }} />
      </Breadcrumbs>
      <Box ref={componentRef}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            component="h1"
            data-fuel-size="3xl"
            fontWeight="semibold"
            sx={{
              paddingBottom: theme.tokens.FuelSpacing6,
              color: theme.tokens.FuelColorTextPrimary,
            }}
            data-testid="invoice-details-header"
          >
            {t("invoices:invoiceDetails.title")}
          </Typography>
          {shouldShowTimeLimitErrorMessage && (
            <Alert variant="error" sx={{ marginBottom: "0.75rem" }}>
              {t("invoices:invoiceDetails.error.message")}
            </Alert>
          )}
        </Box>
        <Grid>
          <SummarySection
            summary={invoice ? invoiceSummaryDetails : []}
            maxNumOfColumns={5}
          >
            <Grid data-testid="summary-attachments">
              <Typography
                variant="label"
                style={{ color: theme.tokens.FuelColorTextTertiary }}
              >
                {t("invoices:invoiceDetails.label.summary.attachments")}
              </Typography>
              <Grid minHeight="24px">
                {isIndiaDealer && (
                  <>
                    <DownloadButton
                      downloadButtonProps={{
                        downloadFunction,
                        isRealTimeDownload: true,
                        shouldDisableDownloadButton: false,
                        shouldDisableTooltip: true,
                        setShowUnauthorizedError,
                        buttonText: "GST_Annexure_Invoice.pdf",
                        variant: "text-inline",
                        isAttachmentButton: true,
                        objTypeCode: INDIA_GST_ANNEXURE_INVOICE,
                      }}
                    />
                    <Typography
                      display="inline"
                      color="primary"
                      style={{
                        position: "relative",
                        top: "-5px",
                        margin: "10px",
                      }}
                    >
                      |
                    </Typography>
                    <DownloadButton
                      downloadButtonProps={{
                        downloadFunction,
                        isRealTimeDownload: true,
                        shouldDisableDownloadButton: false,
                        shouldDisableTooltip: true,
                        setShowUnauthorizedError,
                        buttonText: "GST_Trade_Commercial_Invoice.pdf",
                        variant: "text-inline",
                        isAttachmentButton: true,
                        objTypeCode: INDIA_GST_TRADE_INVOICE,
                      }}
                    />
                  </>
                )}
                {invoiceTypeHasNfeAttachment(
                  invoice?.invoiceType,
                  dealerLocation
                ) &&
                  invoice?.nfeNumber && (
                    <DownloadButton
                      downloadButtonProps={{
                        downloadFunction,
                        isRealTimeDownload: true,
                        shouldDisableDownloadButton: false,
                        shouldDisableTooltip: true,
                        setShowUnauthorizedError,
                        buttonText: `${invoice.nfeNumber}.pdf`,
                        variant: "text-inline",
                        isAttachmentButton: true,
                        objTypeCode: BRAZIL_NFE_ATTACHMENT,
                        fileName: invoice.nfeNumber,
                      }}
                    />
                  )}
                {isBrazilExportDealer && invoice?.officialDocumentNumber && (
                  <>
                    <DownloadButton
                      downloadButtonProps={{
                        downloadFunction: downloadAttachmentFunction,
                        isRealTimeDownload: true,
                        shouldDisableDownloadButton: false,
                        shouldDisableTooltip: true,
                        setShowUnauthorizedError,
                        buttonText: `${invoice.officialDocumentNumber}_INV.pdf`,
                        variant: "text-inline",
                        isAttachmentButton: true,
                        fileName: `${invoice.officialDocumentNumber} INV.pdf`,
                      }}
                    />
                    <Typography
                      display="inline"
                      color="primary"
                      style={{
                        position: "relative",
                        top: "-5px",
                        margin: "10px",
                      }}
                    >
                      |
                    </Typography>
                    <DownloadButton
                      downloadButtonProps={{
                        downloadFunction: downloadAttachmentFunction,
                        isRealTimeDownload: true,
                        shouldDisableDownloadButton: false,
                        shouldDisableTooltip: true,
                        setShowUnauthorizedError,
                        buttonText: `${invoice.officialDocumentNumber}_PL.pdf`,
                        variant: "text-inline",
                        isAttachmentButton: true,
                        fileName: `${invoice.officialDocumentNumber} PL.pdf`,
                      }}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </SummarySection>
          <Grid
            container
            display="grid"
            gridTemplateColumns="50% 50%"
            gridTemplateAreas="'shipTo SoldTo' 'shippingInfo invoiceSummary'"
            rowSpacing={0}
            columnSpacing={0}
          >
            <Address
              shippingAddress={invoice?.shipToAddress}
              showLoader={loadingInvoiceDetails}
              type={t("invoices:invoiceDetails.label.shipTo")}
              pageName="invoice"
              sx={{
                gridArea: "shipTo",
                borderRadius: "8px 0px 0px 0px",
                boxShadow: "none",
                outline: "1px solid",
                outlineColor: theme.tokens.FuelColorBorderLight,
              }}
              contentSx={{
                height: "11rem",
              }}
            />
            <Address
              shippingAddress={invoice?.soldToAddress}
              showLoader={loadingInvoiceDetails}
              type={t("invoices:invoiceDetails.label.shopTo")}
              pageName="invoice"
              sx={{
                gridArea: "soldTo",
                borderRadius: "0px 8px 0px 0px",
                boxShadow: "none",
                outline: "1px solid",
                outlineColor: theme.tokens.FuelColorBorderLight,
              }}
              contentSx={{
                height: "11rem",
              }}
            />
            <ShippingInformation
              shippingAddress={invoice?.shippingInformation}
              showLoader={loadingInvoiceDetails}
              type={t("invoices:invoiceDetails.label.shippingInformation")}
              pageName="invoice"
              sx={{
                gridArea: "shippingInfo",
                borderRadius: "0px 0px 0px 8px",
                boxShadow: "none",
                outline: "1px solid",
                outlineColor: theme.tokens.FuelColorBorderLight,
              }}
              carrierDetails={invoice ? invoice.carrierDetails : ""}
            />
            <DetailedInvoiceSummary
              currency={invoice?.currency}
              discounts={invoice?.discounts}
              surcharges={invoice?.surcharges}
              palletCharges={invoice?.palletCharges}
              freightCharges={invoice?.freightCharges}
              flashPlusCharges={invoice?.flashPlusCharges}
              environmentFees={invoice?.envFees}
              canadaTaxDetails={invoice?.canadaTaxDetails}
              partsSubtotal={invoice?.partsSubtotal}
              tax={invoice?.tax}
              total={invoice?.total}
              isDataPresent={!loadingInvoiceDetails && !!invoice}
              showLoader={loadingInvoiceDetails}
              sx={{
                gridArea: "invoiceSummary",
                borderRadius: "0px 0px 8px 0px",
                boxShadow: "none",
                outline: "1px solid",
                outlineColor: theme.tokens.FuelColorBorderLight,
              }}
            />
          </Grid>
          <Grid width="100%">
            <InvoiceLineItems
              isOrderInvoice={invoice?.invoiceType === "Order Invoice"}
              sendRef={componentRef}
              countryCurrency={invoice?.currency}
              reversed={invoice?.reversed}
              invoiceLineItems={invoiceLineItems}
              error={error}
              reload={reload}
              loading={loadingInvoiceLineItems}
              timeLimit={invoice?.timeLimit}
              isVendorDirect={invoice?.isVendorDirect}
              onDownloadToExcelClick={() => {
                const rowData = buildInvoiceLineItemData({
                  headerTexts: {
                    invoiceNumberHeaderTexts: {
                      invoiceNumberText: t(
                        "invoices:invoiceDetails.label.summary.invoiceNumber"
                      ),
                      newInvoiceNumberText: t(
                        "invoices:invoiceDetails.label.summary.originalInvoiceNumber"
                      ),
                    },
                    nfeNumberHeaderTexts: {
                      nfeNumberText: t(
                        "invoices:invoiceDetails.label.summary.nfeNumber"
                      ),
                      newNfeNumberText: t(
                        "invoices:invoiceDetails.label.summary.newNfeNumber"
                      ),
                    },
                    lineItemsHeaderTexts: {
                      documentNumber: t(
                        "invoices:invoiceLineItems.header.documentNumber"
                      ),
                      dealerReference: t(
                        "invoices:invoiceLineItems.header.dealerReference"
                      ),
                      handlingUnit: t(
                        "invoices:invoiceLineItems.header.handlingUnit"
                      ),
                      partNumber: t(
                        "invoices:invoiceLineItems.header.partNumber"
                      ),
                      description: t(
                        "invoices:invoiceLineItems.header.description"
                      ),
                      quanitity: t("invoices:invoiceLineItems.header.quantity"),
                      unitPrice: t(
                        "invoices:invoiceLineItems.header.unitPrice"
                      ),
                      extendedDiscount: t(
                        "invoices:invoiceLineItems.header.extendedDiscount"
                      ),
                      surcharge: t(
                        "invoices:invoiceLineItems.header.surcharges"
                      ),
                      freightCost: t(
                        "invoices:invoiceLineItems.header.freightCost"
                      ),
                      extendedAmount: t(
                        "invoices:invoiceLineItems.header.extendedAmount"
                      ),
                    },
                  },
                  invoiceNumber: invoice?.invoiceNumber ?? "",
                  originalInvoiceNumber: invoice?.originalInvoiceNum ?? "",
                  nfeNumber: invoice?.nfeNumber ?? "",
                  originalNfeNumber: invoice?.originalNfeNumber ?? "",
                  includeNFE: isBrazilDealer,
                  invoiceLineItems: invoiceLineItems ?? [],
                  showFlashPlusCharge: isUSDealer || isCanadaDealer,
                  isCreditOrDebitMemo: !isOrderInvoiceType(
                    invoice?.invoiceType ?? ""
                  ),
                });

                const csv = rowData
                  .map((data) => Object.values(data).join(","))
                  .join("\r\n");

                const blob = new Blob([csv], { type: "text/csv" });

                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `${invoice?.invoiceNumber}.csv`;

                link.click();
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

export default InvoiceDetails;
